import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import budgetService from 'services/budgetService';
import clientService from 'services/clientService';

// Componentes del dashboard
import PerformanceGreetings from './dashboard/PerformanceGreetings';
import PerformanceStats from './dashboard/PerformanceStats';
import InvestmentRevenue from './dashboard/InvestmentRevenue';
import BudgetForecast from './dashboard/BudgetForecast';
import TopClients from './dashboard/TopClients';
import BudgetVsTarget from './dashboard/BudgetVsTarget';
import RecentOrders from './dashboard/RecentOrders';
import ClientAnalysis from './dashboard/ClientAnalysis';
import PerformanceAlerts from './dashboard/PerformanceAlerts';

const ExecutiveDashboard = () => {
  // Estados principales
  const [orders, setOrders] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados de filtros
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [dateRangePreset, setDateRangePreset] = useState('thisMonth');

  // Presets de fechas
  const datePresets = {
    today: {
      label: 'Hoy',
      range: [new Date(), new Date()]
    },
    thisWeek: {
      label: 'Esta semana',
      range: [
        new Date(new Date().setDate(new Date().getDate() - new Date().getDay())),
        new Date(new Date().setDate(new Date().getDate() + (6 - new Date().getDay())))
      ]
    },
    thisMonth: {
      label: 'Este mes',
      range: [
        new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
      ]
    },
    thisQuarter: {
      label: 'Este trimestre',
      range: [
        new Date(new Date().getFullYear(), Math.floor(new Date().getMonth() / 3) * 3, 1),
        new Date(new Date().getFullYear(), Math.floor(new Date().getMonth() / 3) * 3 + 3, 0)
      ]
    },
    thisYear: {
      label: 'Este año',
      range: [
        new Date(new Date().getFullYear(), 0, 1),
        new Date(new Date().getFullYear(), 11, 31)
      ]
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    loadInitialData();
  }, []);

  // Aplicar preset de fecha por defecto
  useEffect(() => {
    if (dateRangePreset && datePresets[dateRangePreset]) {
      setDateRange(datePresets[dateRangePreset].range);
    }
  }, [dateRangePreset]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Cargar datos del dashboard de forma optimizada
      const [clientsResult, dashboardData] = await Promise.all([
        clientService.getAllClients(),
        budgetService.getDashboardSummary() // Nuevo endpoint que devolverá datos agregados
      ]);

      if (clientsResult.success) {
        setClients(clientsResult.clients || []);
      }

      if (dashboardData.success) {
        setOrders(dashboardData.orders || []);
        setAssignments(dashboardData.assignments || []);
      } else {
        setError('Error al cargar los datos del dashboard');
        toast.error('Error al cargar los datos del dashboard');
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Error de conexión al cargar los datos');
      toast.error('Error de conexión al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  // Obtener business units únicos
  const businessUnits = useMemo(() => {
    return [...new Set(orders.map(order => order.businessUnit).filter(Boolean))].sort();
  }, [orders]);

  // Filtrar datos según los filtros aplicados
  const filteredData = useMemo(() => {
    let filtered = orders;

    // Filtro por cliente
    if (selectedClient) {
      filtered = filtered.filter(order => order.clientId === parseInt(selectedClient));
    }

    // Filtro por business unit
    if (selectedBusinessUnit) {
      filtered = filtered.filter(order => order.businessUnit === selectedBusinessUnit);
    }

    // Filtro por rango de fechas
    if (dateRange[0] && dateRange[1]) {
      const startDate = new Date(dateRange[0]);
      const endDate = new Date(dateRange[1]);
      
      filtered = filtered.filter(order => {
        const orderStart = new Date(order.dateFrom);
        const orderEnd = new Date(order.dateTo);
        
        // La orden debe tener alguna intersección con el rango seleccionado
        return orderStart <= endDate && orderEnd >= startDate;
      });
    }

    // Filtro por estado
    if (selectedStatus !== 'all') {
      const now = new Date();
      filtered = filtered.filter(order => {
        const orderStart = new Date(order.dateFrom);
        const orderEnd = new Date(order.dateTo);
        
        switch (selectedStatus) {
          case 'active':
            return orderStart <= now && orderEnd >= now;
          case 'completed':
            return orderEnd < now;
          case 'upcoming':
            return orderStart > now;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [orders, selectedClient, selectedBusinessUnit, dateRange, selectedStatus]);

  // Filtrar asignaciones correspondientes
  const filteredAssignments = useMemo(() => {
    const filteredOrderIds = filteredData.map(order => order.id);
    return assignments.filter(assignment => filteredOrderIds.includes(assignment.insertionOrderId));
  }, [assignments, filteredData]);

  // Manejar cambio de preset de fecha
  const handleDatePresetChange = (preset) => {
    setDateRangePreset(preset);
  };

  // Manejar cambio de rango de fechas personalizado
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setDateRangePreset(''); // Limpiar preset cuando se selecciona rango personalizado
  };

  // Limpiar filtros
  const clearFilters = () => {
    setSelectedClient('');
    setSelectedBusinessUnit('');
    setSelectedStatus('all');
    setDateRangePreset('thisMonth');
  };

  if (loading) {
    return (
      <Container fluid className="p-0">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando dashboard...</span>
          </div>
          <p className="mt-3 text-muted">Cargando datos del dashboard...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-0">
        <div className="text-center py-5">
          <FontAwesomeIcon icon="exclamation-triangle" className="text-warning fs-1 mb-3" />
          <h5 className="text-danger">Error al cargar el dashboard</h5>
          <p className="text-muted">{error}</p>
          <Button variant="primary" onClick={loadInitialData}>
            <FontAwesomeIcon icon="sync-alt" className="me-2" />
            Reintentar
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="p-0">
      {/* Header con saludo y filtros */}
      <PerformanceGreetings 
        totalOrders={filteredData.length}
        dateRange={dateRange}
        onDatePresetChange={handleDatePresetChange}
        selectedPreset={dateRangePreset}
        datePresets={datePresets}
      />

      {/* Filtros avanzados */}
      <Card className="mb-3">
        <Card.Body className="py-3">
          <Row className="g-3 align-items-end">
            <Col lg={2} md={3} sm={6}>
              <Form.Label className="mb-1 fs-10 fw-semibold text-600">Cliente</Form.Label>
              <Form.Select
                size="sm"
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
              >
                <option value="">Todos los clientes</option>
                {clients.map(client => (
                  <option key={client.ClientID} value={client.ClientID}>
                    {client.ClientName}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col lg={2} md={3} sm={6}>
              <Form.Label className="mb-1 fs-10 fw-semibold text-600">Business Unit</Form.Label>
              <Form.Select
                size="sm"
                value={selectedBusinessUnit}
                onChange={(e) => setSelectedBusinessUnit(e.target.value)}
              >
                <option value="">Todas las unidades</option>
                {businessUnits.map(bu => (
                  <option key={bu} value={bu}>{bu}</option>
                ))}
              </Form.Select>
            </Col>

            <Col lg={2} md={3} sm={6}>
              <Form.Label className="mb-1 fs-10 fw-semibold text-600">Estado</Form.Label>
              <Form.Select
                size="sm"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="all">Todas</option>
                <option value="active">Activas</option>
                <option value="completed">Completadas</option>
                <option value="upcoming">Próximas</option>
              </Form.Select>
            </Col>

            <Col lg={3} md={3} sm={6}>
              <Form.Label className="mb-1 fs-10 fw-semibold text-600">Rango Personalizado</Form.Label>
              <DatePicker
                selected={dateRange[0]}
                onChange={handleDateRangeChange}
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                selectsRange
                dateFormat="dd/MM/yyyy"
                className="form-control form-control-sm"
                placeholderText="Seleccionar rango"
                isClearable
              />
            </Col>

            <Col lg={3} md={12}>
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={clearFilters}
                >
                  <FontAwesomeIcon icon="times" className="me-1" />
                  Limpiar Filtros
                </Button>
                <Button 
                  variant="outline-success" 
                  size="sm"
                  onClick={loadInitialData}
                >
                  <FontAwesomeIcon icon="sync-alt" className="me-1" />
                  Actualizar
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* KPIs principales */}
      <PerformanceStats 
        orders={filteredData}
        assignments={filteredAssignments}
      />

      {/* Gráficos y análisis */}
      <Row className="g-3 mb-3">
        <Col xxl={9}>
          <InvestmentRevenue 
            orders={filteredData}
            assignments={filteredAssignments}
          />
        </Col>
        <Col xxl={3}>
          <TopClients 
            orders={filteredData}
            assignments={filteredAssignments}
            clients={clients}
          />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={12} xxl={8}>
          <BudgetForecast 
            orders={filteredData}
            assignments={filteredAssignments}
            clients={clients}
          />
        </Col>
        <Col xxl={4}>
          <PerformanceAlerts 
            orders={filteredData}
            assignments={filteredAssignments}
          />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col xxl={6}>
          <BudgetVsTarget 
            orders={filteredData}
            assignments={filteredAssignments}
            clients={clients}
          />
        </Col>
        <Col xxl={6}>
          <ClientAnalysis 
            orders={filteredData}
            assignments={filteredAssignments}
            clients={clients}
          />
        </Col>
      </Row>

      {/* Tablas de detalle */}
      <Row className="g-3">
        <Col lg={12}>
          <RecentOrders 
            orders={filteredData}
            assignments={filteredAssignments}
            clients={clients}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default ExecutiveDashboard; 